/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastData, ToastStore } from 'src/luis/core/stores/ToastStore';
import { ToastStatus } from 'src/luis/types/stores/events/ToastStatus';

const eventBus = getEventBus();

export type AddToastParameters = {
    /**
     * The toast data to use.
     */
    toastData?: {
        /**
         * The toast data to display when the transaction
         * starts running.
         */
        running?: ToastEventData;

        /**
         * The toast data to display when the transaction
         * has completed execution.
         */
        completed?: ToastEventData;

        /**
         * The toast data to display when the transaction
         * has failed.
         */
        failed?: ToastEventData;

        /**
         * If the toast should not pop up, but go straight to toast history.
         */
        quiet?: boolean;
    };
};

/**
 * A helper type to use for the `AddToastParameters` type.
 */
type ToastEventData = Pick<ToastStore, 'title'> & Partial<Pick<ToastStore, 'content' | 'actions'>>;

export const parseErrorXmlResponse = (errorName: string, errorContent: string): Error => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(errorContent, 'text/xml');

    const error = new Error(xmlDoc.getElementsByTagName('Error')[0].textContent);
    error.name = errorName;

    return error;
};

/**
 * Wraps the transaction with toast events based on the state
 * of the transaction.
 *
 * @param input The toast data to display.
 * @throws The original error is thrown again.
 */
export const addToast = (input: AddToastParameters) => async <T>(transaction: Promise<T>): Promise<T> => {
    if (!input) {
        return transaction;
    }

    const { toastData } = input;
    let toastId: string;
    let data: T;

    const addOrUpdateToast = (payload: ToastData) => {
        if (toastId) {
            eventBus.emit('updateToast', { id: toastId, toast: payload });
        } else {
            toastId = getId();
            eventBus.emit('addToast', { id: toastId, ...payload });
        }
    };

    if (!toastData) {
        return transaction;
    }

    try {
        if (toastData.running) {
            toastId = getId();
            eventBus.emit('addToast', { id: toastId, ...toastData.running, quiet: toastData.quiet });
        }

        data = await transaction;

        if (toastData.running || toastData.completed) {
            addOrUpdateToast({ status: <ToastStatus>'completed', ...toastData.completed, quiet: toastData.quiet });
        }

        return data;
    } catch (error) {
        let content: React.ReactNode;

        if (toastData.failed && toastData.failed.content) {
            content = toastData.failed.content;
        } else if (<unknown>(<Error>error).message instanceof Blob) {
            content = parseErrorXmlResponse((<Error>error).name, await (<Blob>(<unknown>(<Error>error).message)).text()).message;
        } else if (<unknown>(<Error>error).message instanceof Array) {
            content = (<Array<{ error: { code: string; message: string } }>>(<unknown>(<Error>error).message))
                .reduce((accErr, currentErr) => accErr.concat(`${currentErr.error.code}: ${currentErr.error.message}\n`), '')
                .trim();
        } else {
            content = (<Error>error).message;
        }

        const title = toastData.failed?.title ?? (<Error>error).name;

        addOrUpdateToast({ status: 'failed', ...toastData.failed, title, content });

        throw error;
    }
};
